/**
 * API Configuration Utility
 * 
 * This handles the API base URL for different deployment scenarios:
 * - Development: Uses REACT_APP_BACKEND_URL from .env
 * - Production with custom domain: Uses relative URLs to avoid CORS issues
 * - Production without custom domain: Uses REACT_APP_BACKEND_URL
 */

// Get the configured backend URL from environment
const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Determines if we should use relative URLs (same-origin requests)
 * This avoids CORS issues when using custom domains
 */
const shouldUseRelativeUrl = () => {
  // In development, always use the configured URL
  if (process.env.NODE_ENV === 'development') {
    return false;
  }
  
  // In production, if the backend URL is different from current origin,
  // but we're on a custom domain, use relative URLs
  try {
    const currentOrigin = window.location.origin;
    const backendOrigin = new URL(configuredBackendUrl).origin;
    
    // If origins match, use relative URLs
    if (currentOrigin === backendOrigin) {
      return true;
    }
    
    // If we're on a custom domain (not emergent.host), use relative URLs
    // because the backend is proxied through the same domain
    if (!currentOrigin.includes('emergent.host') && 
        !currentOrigin.includes('localhost') &&
        !currentOrigin.includes('127.0.0.1')) {
      return true;
    }
    
    return false;
  } catch (e) {
    // If URL parsing fails, fall back to configured URL
    return false;
  }
};

/**
 * Get the API base URL
 * Returns empty string for same-origin requests (relative URLs)
 * Returns the configured backend URL otherwise
 */
export const getApiBaseUrl = () => {
  if (shouldUseRelativeUrl()) {
    return '';
  }
  return configuredBackendUrl;
};

/**
 * Build a full API URL
 * @param {string} path - The API path (e.g., '/api/auth/login')
 * @returns {string} The full URL
 */
export const buildApiUrl = (path) => {
  const baseUrl = getApiBaseUrl();
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

// Export the base URL for backward compatibility
export const API_BASE_URL = getApiBaseUrl();

export default {
  getApiBaseUrl,
  buildApiUrl,
  API_BASE_URL
};
